import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, Loader2, Mic, Square, Camera, Lightbulb, Receipt, ClipboardCheck, CheckCircle2, AlertTriangle, ChevronRight, RefreshCw, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, inputCls, btnPrimary, btnGhost, eur } from "@/lib/studioApi";
import { useDictation } from "@/lib/useDictation";

const URGENCY = { faible: "bg-emerald-50 text-emerald-700 border-emerald-200", normale: "bg-[#EEF1FC] text-[#2F4CA8] border-[#C9D4F4]", urgente: "bg-rose-50 text-rose-700 border-rose-200" };

/* Étape « Diagnostic IA » d'une intervention : symptômes → causes, vérifications, questions, réparation chiffrée → rapport / facture. */
export const DiagnosticCard = ({ interventionId, diagnostic, photosCount = 0, isOwner, signed, onUpdated }) => {
  const navigate = useNavigate();
  const [symptoms, setSymptoms] = useState("");
  const [usePhotos, setUsePhotos] = useState(true);
  const [busy, setBusy] = useState("");
  const [answers, setAnswers] = useState({});
  const { recording, transcribing, toggle } = useDictation((text) => setSymptoms((n) => (n ? `${n}\n${text}` : text)));
  const d = diagnostic;
  const withPhotos = usePhotos && photosCount > 0;
  const canRun = symptoms.trim().length >= 10 || withPhotos || !!d?.symptoms;
  const total = (d?.lines || []).reduce((s, l) => s + l.quantity * l.unit_price, 0);
  const unpriced = (d?.lines || []).filter((l) => !l.priced).length;

  const run = async (extraAnswers) => {
    setBusy("run");
    try {
      const { data } = await studio.post(`/interventions/${interventionId}/diagnostic`, { symptoms, use_photos: withPhotos, answers: extraAnswers || null }, { timeout: 180000 });
      onUpdated({ diagnostic: data });
      setSymptoms("");
      setAnswers({});
      toast.success(extraAnswers ? "Diagnostic affiné avec vos réponses." : "Diagnostic établi : causes, vérifications et réparation proposées.");
    } catch (e) { toast.error(apiError(e, "Diagnostic impossible.")); } finally { setBusy(""); }
  };

  const refine = () => {
    const list = (d.questions || []).map((q) => ({ question: q.question, answer: answers[q.question] })).filter((a) => a.answer);
    if (!list.length) return toast.error("Répondez à au moins une question.");
    run(list);
  };

  const toggleCheck = async (id) => {
    const next = d.checks.map((c) => (c.id === id ? { ...c, done: !c.done } : c));
    onUpdated({ diagnostic: { ...d, checks: next } });
    try { await studio.put(`/interventions/${interventionId}/diagnostic/checks`, { done_ids: next.filter((c) => c.done).map((c) => c.id) }); } catch { /* silencieux */ }
  };

  const apply = async () => {
    setBusy("apply");
    try {
      const { data } = await studio.post(`/interventions/${interventionId}/diagnostic/apply`);
      onUpdated(data);
      toast.success("Réparation reportée dans le rapport : travaux, matériel et constat mis à jour.");
    } catch (e) { toast.error(apiError(e, "Report impossible.")); } finally { setBusy(""); }
  };

  const invoice = async (force = false) => {
    setBusy("invoice");
    try {
      const { data } = await studio.post(`/interventions/${interventionId}/diagnostic/invoice${force ? "?force=true" : ""}`);
      toast.success(`Facture ${data.reference} créée depuis le diagnostic.`);
      navigate(`/studio/factures/${data.id}`);
    } catch (e) {
      if (e?.response?.status === 422 && !force) {
        setBusy("");
        if (window.confirm(`${e.response.data.detail}\n\nFacturer quand même ? Vous pourrez compléter les prix sur la facture.`)) return invoice(true);
        return;
      }
      toast.error(apiError(e, "Facturation impossible."));
      setBusy("");
    }
  };

  const toQuote = async () => {
    setBusy("quote");
    try {
      const { data } = await studio.post(`/interventions/${interventionId}/diagnostic/quote`);
      toast.success(`Devis ${data.number} créé depuis le diagnostic : à faire signer par le client avant travaux.`);
      navigate(`/studio/devis/${data.id}`);
    } catch (e) { toast.error(apiError(e, "Création du devis impossible.")); setBusy(""); }
  };

  return (
    <section data-testid="iv-diagnostic" className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)] sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading inline-flex items-center gap-2 text-base font-semibold text-[#0F2645]">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#0F2645] text-white"><Stethoscope className="h-4 w-4" /></span> Diagnostic IA
        </h2>
        {d?.repair?.urgency && <span data-testid="diag-urgency" className={`rounded-md border px-2 py-0.5 text-[11px] font-bold ${URGENCY[d.repair.urgency]}`}>Urgence {d.repair.urgency}</span>}
      </div>
      <p className="mt-1 text-xs text-[#5C6A87]">Décrivez la panne (ou dictez-la, ou laissez l'IA lire vos photos) : l'assistant classe les causes probables, vous guide dans les vérifications, pose les bonnes questions et prépare la réparation chiffrée avec vos tarifs.</p>

      {!signed && (
        <>
          <textarea data-testid="diag-symptoms" rows={3} className={`${inputCls} mt-3`} placeholder={d ? "Précisez de nouveaux éléments pour relancer le diagnostic…" : "Ex. : chaudière affiche F.28, trois tentatives d'allumage puis mise en sécurité, pression 1,4 bar, gaz OK ailleurs…"} value={symptoms} onChange={(e) => setSymptoms(e.target.value)} />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" data-testid="diag-dictate" onClick={toggle} disabled={!!busy || transcribing} className={`inline-flex min-h-[40px] items-center gap-2 rounded-xl px-4 text-sm font-semibold transition-colors ${recording ? "animate-pulse bg-rose-600 text-white" : "border border-[#E2E7F7] bg-white text-[#0F2645] hover:border-[#3A5CC8]"}`}>
              {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />} {recording ? "Arrêter" : "Dicter"}
            </button>
            <label data-testid="diag-use-photos" className={`inline-flex min-h-[40px] items-center gap-2 rounded-xl border px-3 text-sm ${photosCount ? "border-[#E2E7F7] bg-white text-[#0F2645]" : "border-dashed border-[#E2E7F7] text-[#93A0BC]"}`}>
              <input type="checkbox" checked={withPhotos} disabled={!photosCount} onChange={(e) => setUsePhotos(e.target.checked)} className="h-4 w-4 accent-[#3A5CC8]" /><Camera className="h-4 w-4 text-[#3A5CC8]" /> {photosCount ? `${photosCount} photo${photosCount > 1 ? "s" : ""}` : "Pas de photo"}
            </label>
            <button type="button" data-testid="diag-run" onClick={() => run()} disabled={busy === "run" || !canRun} className={`${btnPrimary} ml-auto`}>
              {busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : d ? <RefreshCw className="h-4 w-4" /> : <Stethoscope className="h-4 w-4" />} {busy === "run" ? "Analyse…" : d ? "Relancer le diagnostic" : "Lancer le diagnostic"}
            </button>
          </div>
        </>
      )}

      {d && (
        <div className="mt-5 space-y-5" style={{ animation: "devFadeUp .25s ease-out" }}>
          <p data-testid="diag-summary" className="rounded-xl bg-[#F6F8FD] px-4 py-3 text-sm leading-relaxed text-[#0F2645]">{d.summary}</p>

          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">Causes probables</p>
              <ul className="mt-2 space-y-2">
                {d.causes.map((c, i) => (
                  <li key={c.label} data-testid={`diag-cause-${i}`} className="rounded-xl border border-[#E2E7F7] p-3">
                    <div className="flex items-center justify-between gap-3 text-sm"><span className="font-semibold text-[#0F2645]">{c.label}</span><span className="shrink-0 font-mono text-xs font-bold text-[#2F4CA8]">{c.probability} %</span></div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#EEF1FC]"><span className="block h-full rounded-full bg-[#3A5CC8]" style={{ width: `${Math.min(100, c.probability)}%` }} /></div>
                    {c.why && <p className="mt-1.5 text-xs text-[#5C6A87]">{c.why}</p>}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">Vérifications sur place</p>
              <ul className="mt-2 space-y-1.5">
                {d.checks.map((c, i) => (
                  <li key={c.id}>
                    <button type="button" data-testid={`diag-check-${i}`} onClick={() => toggleCheck(c.id)} className={`flex w-full items-start gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors ${c.done ? "border-emerald-200 bg-emerald-50" : "border-[#E2E7F7] bg-white hover:border-[#C9D4F4]"}`}>
                      <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-[10px] font-bold ${c.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-[#C9D4F4] text-[#5C6A87]"}`}>{c.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}</span>
                      <span className="min-w-0"><span className={`block text-sm ${c.done ? "text-emerald-900 line-through" : "text-[#0F2645]"}`}>{c.step}</span>{c.expected && <span className="block text-xs text-[#5C6A87]">Attendu : {c.expected}</span>}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {!signed && d.questions?.length > 0 && (
            <div data-testid="diag-questions" className="rounded-2xl border border-[#C9D4F4] bg-[#F6F8FD] p-4">
              <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#2F4CA8]"><Lightbulb className="h-3.5 w-3.5 text-amber-500" /> Pour affiner le diagnostic</p>
              <div className="mt-3 space-y-3">
                {d.questions.map((q, qi) => (
                  <div key={q.question}>
                    <p className="text-sm font-semibold text-[#0F2645]">{q.question}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {q.options.map((o, oi) => (
                        <button key={o} type="button" data-testid={`diag-q${qi}-opt${oi}`} onClick={() => setAnswers((a) => ({ ...a, [q.question]: o }))} className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${answers[q.question] === o ? "border-[#3A5CC8] bg-[#3A5CC8] text-white" : "border-[#E2E7F7] bg-white text-[#0F2645] hover:border-[#C9D4F4]"}`}>{o}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" data-testid="diag-refine" onClick={refine} disabled={busy === "run"} className={`${btnGhost} mt-3`}>{busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4 text-[#3A5CC8]" />} Affiner avec mes réponses</button>
            </div>
          )}

          <div data-testid="diag-repair" className="rounded-2xl border border-[#E2E7F7] p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">Réparation proposée</p>
            <p className="mt-1 text-sm font-semibold text-[#0F2645]">{d.repair.label || "—"}</p>
            {d.repair.advice_client && <p className="mt-1 text-xs text-[#5C6A87]">Pour le client : {d.repair.advice_client}</p>}
            <ul className="mt-3 divide-y divide-[#E2E7F7] rounded-xl border border-[#E2E7F7]">
              {d.lines.map((l, i) => (
                <li key={l.id} data-testid={`diag-line-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                  <span className="basis-full text-[#0F2645] sm:basis-auto sm:flex-1">{l.description}</span>
                  <span className="font-mono text-xs text-[#5C6A87]">{l.quantity} {l.unit}</span>
                  <span className={`ml-auto font-mono text-sm font-bold tabular-nums sm:ml-0 ${l.priced ? "text-[#0F2645]" : "text-amber-700"}`}>{l.priced ? eur(l.quantity * l.unit_price) : "à chiffrer"}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-[#5C6A87]">Estimation HTVA <span data-testid="diag-total" className="font-mono font-bold text-[#0F2645]">{eur(total)}</span>{unpriced > 0 && <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> {unpriced} ligne{unpriced > 1 ? "s" : ""} sans tarif</span>}</p>
              <div className="flex flex-wrap gap-2">
                {!signed && <button type="button" data-testid="diag-apply" onClick={apply} disabled={!!busy || d.applied} className={btnGhost}>{busy === "apply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4 text-[#3A5CC8]" />} {d.applied ? "Reporté dans le rapport" : d.applied_ids?.length ? "Mettre à jour le rapport" : "Reporter dans le rapport"}</button>}
                {d.quote_id ? (
                  <button type="button" data-testid="diag-open-quote" onClick={() => navigate(`/studio/devis/${d.quote_id}`)} className={btnGhost}><FileSignature className="h-4 w-4 text-[#3A5CC8]" /> Devis {d.quote_number || ""}</button>
                ) : (
                  <button type="button" data-testid="diag-quote" onClick={toQuote} disabled={!!busy || !d.lines.length} className={btnGhost} title="À faire valider et signer par le client avant les travaux">{busy === "quote" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4 text-[#3A5CC8]" />} Devis à signer</button>
                )}
                {isOwner && (d.invoice_id ? (
                  <button type="button" data-testid="diag-open-invoice" onClick={() => navigate(`/studio/factures/${d.invoice_id}`)} className={btnPrimary}><Receipt className="h-4 w-4" /> Ouvrir la facture</button>
                ) : (
                  <button type="button" data-testid="diag-invoice" onClick={() => invoice(false)} disabled={!!busy || !d.lines.length} className={btnPrimary}>{busy === "invoice" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />} Convertir en facture</button>
                ))}
              </div>
            </div>
          </div>

          {d.suggestions?.length > 0 && (
            <div data-testid="diag-suggestions">
              <p className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]"><Lightbulb className="h-3.5 w-3.5 text-amber-500" /> Et ensuite ?</p>
              <div className="flex flex-wrap gap-1.5">
                {d.suggestions.map((s, i) => <button key={s} type="button" data-testid={`diag-suggestion-${i + 1}`} onClick={() => setSymptoms((n) => (n ? `${n}\n${s}` : s))} className="rounded-lg border border-[#E2E7F7] bg-[#F6F8FD] px-2.5 py-1.5 text-left text-xs font-medium text-[#0F2645] transition-colors hover:border-[#3A5CC8] hover:text-[#2F4CA8]">{s}</button>)}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
